import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";

import { account } from "../appwrite/config";
import { useAuth } from "../context/AuthContext";

import "../assets/css/Dashboard.css";

const Dashboard = () => {
  const {
    role,
    user,
    loading: authLoading,
    isLoggedIn: authIsLoggedIn,
  } = useAuth();

  const navigate = useNavigate();

  const [alert, setAlert] = useState({
    type: "",
    message: "",
  });

  const [isLoggedIn, setIsLoggedIn] = useState(true);
  const [loading, setLoading] = useState(true);

  const [stats, setStats] = useState({
    artists: 0,
    agents: 0,
    bookings: 0,
  });

  /*
   * ---------------------------------------------------------
   * Remove any old document class
   * ---------------------------------------------------------
   */
  useEffect(() => {
    document.documentElement.className = "";
  }, []);

  /*
   * ---------------------------------------------------------
   * Fetch dashboard data after authentication is ready
   * ---------------------------------------------------------
   */
  useEffect(() => {
    const token = sessionStorage.getItem("JWT_TOKEN");

    if (
      !authLoading &&
      authIsLoggedIn &&
      token &&
      role
    ) {
      getDashboardData();
    }

    if (!authLoading && !authIsLoggedIn) {
      setIsLoggedIn(false);
      setLoading(false);
    }
  }, [authLoading, authIsLoggedIn, role, user]);

  /*
   * ---------------------------------------------------------
   * Authentication headers
   * ---------------------------------------------------------
   */
  const getAuthHeaders = () => {
    const token = sessionStorage.getItem("JWT_TOKEN");

    return {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${ token } `,
      },
    };
  };

  /*
   * ---------------------------------------------------------
   * Logout
   * ---------------------------------------------------------
   */
  const logout = async () => {
    try {
      await account.deleteSession("current");
    } catch (error) {
      console.error("Logout error:", error);
    }

    sessionStorage.clear();

    setIsLoggedIn(false);

    navigate("/");

    window.location.reload();
  };

  /*
   * ---------------------------------------------------------
   * Dashboard API loader
   * ---------------------------------------------------------
   */
  const getDashboardData = async () => {
    setLoading(true);

    try {
      const promises = [];

      if (role === "admin") {
        promises.push(
          getAllArtists(),
          getAllAgents(),
          getAllBookings()
        );
      }

      if (role === "artist") {
        promises.push(
          getAllArtists(),
          getAllBookings()
        );
      }

      if (
        role === "agent" ||
        role === "organization"
      ) {
        promises.push(
          getAllArtists(),
          getAllAgents(),
          getAllBookings()
        );
      }

      await Promise.all(promises);
    } catch (error) {
      console.error("Dashboard data error:", error);
    } finally {
      setLoading(false);
    }
  };

  /*
   * ---------------------------------------------------------
   * Get Artists
   * ---------------------------------------------------------
   */
  const getAllArtists = async () => {
    try {
      console.log("Fetching artists...");

      const response = await axios.get(
        `${ process.env.REACT_APP_API_URL }/artists`,
getAuthHeaders()
      );

const data =
  response?.data?.data ||
  response?.data?.documents ||
  [];

console.log("Artists Data:", data);

/*
 * Store Artist IDs
 */
const artistIds = Array.isArray(data)
  ? data.map((artist) => artist.$id)
  : [];

sessionStorage.setItem(
  "artistIds",
  JSON.stringify(artistIds)
);

/*
 * Prefer API pagination total
 * instead of data.length
 */
const artistCount =
  response?.data?.pagination?.total ??
  response?.data?.total ??
  data.length ??
  0;

console.log("Artist Count:", artistCount);

setStats((prev) => ({
  ...prev,
  artists: artistCount,
}));
    } catch (error) {
  console.error("Get artists error:", error);

  const apiError =
    error?.response?.data?.error?.message ||
    error?.response?.data?.message ||
    "Something went wrong while fetching artists.";

  setAlert({
    type: "error",
    message: apiError,
  });
}
  };

/*
 * ---------------------------------------------------------
 * Get Agents
 * ---------------------------------------------------------
 */
const getAllAgents = async () => {
  try {
    console.log("Fetching agents...");

    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/agents`,
      {
        ...getAuthHeaders(),
        params: {
          userId: role === "admin" ? undefined : user?.$id,
        },
      }
    );

    const data =
      response?.data?.documents ||
      response?.data?.data ||
      [];

    console.log("Agents Data:", data);

    const agentCount =
      response?.data?.pagination?.total ??
      response?.data?.total ??
      (Array.isArray(data) ? data.length : 0);

    console.log("Agent Count:", agentCount);

    setStats((prev) => ({
      ...prev,
      agents: agentCount,
    }));
  } catch (error) {
    console.error("Get agents error:", error);

    const apiError =
      error?.response?.data?.error?.message ||
      error?.response?.data?.message ||
      "Something went wrong while fetching agents.";

    setAlert({
      type: "error",
      message: apiError,
    });
  }
};

/*
 * ---------------------------------------------------------
 * Get Bookings
 * ---------------------------------------------------------
 */
const getAllBookings = async () => {
  try {
    console.log("Fetching bookings...");

    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/bookings`,
      {
        ...getAuthHeaders(),
        params: {
          userId: role === "admin" ? undefined : user?.$id,
        },
      }
    );

    const data =
      response?.data?.documents ||
      response?.data?.data ||
      [];

    console.log("Bookings Data:", data);

    /*
     * IMPORTANT:
     * Do NOT send limit: 0.
     *
     * Your API/Appwrite rejects limit 0.
     */
    const bookingCount =
      response?.data?.pagination?.total ??
      response?.data?.total ??
      (Array.isArray(data) ? data.length : 0);

    console.log("Booking Count:", bookingCount);

    setStats((prev) => ({
      ...prev,
      bookings: bookingCount,
    }));
  } catch (error) {
    console.error("Get bookings error:", error);

    const apiError =
      error?.response?.data?.error?.message ||
      error?.response?.data?.message ||
      "Something went wrong while fetching bookings.";

    setAlert({
      type: "error",
      message: apiError,
    });
  }
};

/*
 * ---------------------------------------------------------
 * Render
 * ---------------------------------------------------------
 */
return (
  <section className="is-title-bar">
    <div className="flex flex-col md:flex-row items-center justify-between space-y-6 md:space-y-0 mb-6">
      <ul>
        <li>Admin</li>
        <li>Dashboard</li>
      </ul>
    </div>

    <div className="card dashboard-main-card">
      <header className="card-header">
        <p className="card-header-title">
          <span className="icon">
            <i className="mdi mdi-finance"></i>
          </span>

          Welcome to your Dashboard
        </p>
      </header>

      <div className="card-content">
        {loading ? (
          <div className="dashboard-loader">
            <div className="dashboard-spinner"></div>
          </div>
        ) : (
          <div className="dashboard-grid">

            {/* ------------------------------------------------
                  ARTISTS
              ------------------------------------------------ */}
            {[
              "admin",
              "artist",
              "agent",
              "organization",
            ].includes(role) && (
                <Link
                  to={isLoggedIn ? "/artists" : "/"}
                >
                  <div className="dashboard-stat-card business-card">
                    <div className="dashboard-card-icon">
                      <i className="mdi mdi-store"></i>
                    </div>

                    <div>
                      <p className="dashboard-card-title">
                        Artists
                      </p>

                      <h2 className="dashboard-card-count">
                        {stats.artists}
                      </h2>
                    </div>
                  </div>
                </Link>
              )}

            {/* ------------------------------------------------
                  AGENTS
              ------------------------------------------------ */}
            {[
              "admin",
              "agent",
              "organization",
            ].includes(role) && (
                <Link
                  to={isLoggedIn ? "/agents" : "/"}
                >
                  <div className="dashboard-stat-card category-card">
                    <div className="dashboard-card-icon">
                      <i className="mdi mdi-shape"></i>
                    </div>

                    <div>
                      <p className="dashboard-card-title">
                        Agents
                      </p>

                      <h2 className="dashboard-card-count">
                        {stats.agents}
                      </h2>
                    </div>
                  </div>
                </Link>
              )}

            {/* ------------------------------------------------
                  BOOKINGS
              ------------------------------------------------ */}
            {[
              "admin",
              "artist",
              "agent",
              "organization",
            ].includes(role) && (
                <Link
                  to={isLoggedIn ? "/bookings" : "/"}
                >
                  <div className="dashboard-stat-card coupon-card">
                    <div className="dashboard-card-icon">
                      <i className="mdi mdi-ticket-percent"></i>
                    </div>

                    <div>
                      <p className="dashboard-card-title">
                        Bookings
                      </p>

                      <h2 className="dashboard-card-count">
                        {stats.bookings}
                      </h2>
                    </div>
                  </div>
                </Link>
              )}

          </div>
        )}

        {alert.message && (
          <div className={`alert ${alert.type}`}>
            {alert.message}
          </div>
        )}
      </div>
    </div>
  </section>
);
};

export default Dashboard;